/**
 * HLOS-134 (era HLOS-133.1) — VaultAdapter (implementação de VaultAPI).
 *
 * Implementação concreta de VaultAPI que delega para o `Vault` existente
 * em `./store.ts`. Lazy-loaded via dynamic import para preservar o HLOS-131
 * (vault chunk NÃO aparece no entry bundle).
 *
 * Pattern: dependency injection via módulo singleton com setter.
 * - `getVaultAPI()` retorna a instância singleton
 * - `setVaultAPI(api)` permite override em testes
 * - Default instance lazy-inicializa VaultAdapter no primeiro uso
 *
 * Constitucional:
 * - CL-07 (local-first): VaultAdapter preserva OPFS via Vault subjacente
 * - CL-09 (Explicabilidade): Adapter documentado + tested
 *
 * Refs:
 * - HLOS-134 (ADR-0026)
 * - REQ-012 (UI Shell Architectural Decoupling)
 */

import type { VaultAPI, InitOptions, InitResult, CreateResult } from "./contract";
import { storageFactoryAsync, getBackendId } from "./storage";
import { waitForIOSBridge } from "./backends/capacitor-ios";

/**
 * Singleton instance da API ativa. Inicializada lazily no primeiro getVaultAPI().
 * Substituível via setVaultAPI() (usado em testes).
 */
let _vaultAPI: VaultAPI | null = null;

/**
 * Define a API ativa. Útil para testes (mock) e boot explícito.
 * Se nunca chamada, getVaultAPI() cria VaultAdapter lazy no primeiro uso.
 */
export function setVaultAPI(api: VaultAPI): void {
  _vaultAPI = api;
}

/** Reseta a API singleton (apenas para testes). */
export function resetVaultAPI(): void {
  _vaultAPI = null;
}

/**
 * Retorna a API singleton. Cria VaultAdapter lazily no primeiro uso.
 * Auth consome APENAS esta função — nunca importa vault/store diretamente.
 */
export async function getVaultAPI(): Promise<VaultAPI> {
  if (!_vaultAPI) {
    _vaultAPI = await createVaultAdapter();
  }
  return _vaultAPI;
}

/**
 * Cria uma instância VaultAdapter. Lazy-loads o módulo vault via dynamic
 * import para preservar o HLOS-131 (vault chunk fora do entry bundle).
 */
export async function createVaultAdapter(): Promise<VaultAPI> {
  // Lazy load vault module (HLOS-131): evita entry chunk eager
  const vaultModule = await import("./store");
  return new VaultAdapter(vaultModule);
}

/**
 * VaultAdapter — implementação de VaultAPI que delega para o Vault subjacente.
 *
 * @internal Usado via getVaultAPI() — auth store não deve importar diretamente.
 */
class VaultAdapter implements VaultAPI {
  private readonly module: typeof import("./store");
  private currentVault: import("./store").Vault | null = null;

  constructor(module: typeof import("./store")) {
    this.module = module;
  }

  async init(options?: InitOptions): Promise<InitResult> {
    // HLOS-172 FIX v4: aguardar Swift WKScriptMessageHandler registrar antes de
    // selecionar backend. Sem isso, race condition no cold start: TS init()
    // é chamado antes do Swift `AppleLLMRuntimeBridge.attach()` completar, e
    // `isCapacitorIOSAvailable()` retorna false (bridge null). Aí
    // storageFactory() cai em OPFS, que falha em iOS 26+ (sandbox deny).
    //
    // Resultado do bug: vaultExists=false → app vai para setupMode ("Boas
    // vindas") em vez de lockedMode ("Bem-vindo de volta"). O workaround
    // que o usuário descobriu (LGPD + back) força reload da raiz, quando
    // o bridge já está registrado. Este fix elimina o workaround.
    if (!options?.storage && options?.backend !== "memory") {
      await waitForIOSBridge({timeoutMs: 1500, intervalMs: 100});
    }

    // Dynamic import — preserva HLOS-131 (vault lazy)
    const paths = await import("./paths");
    const vaultFile = paths.vaultFilePath(VAULT_USER_ID);

    let storage = options?.storage as import("./store").VaultStorage | undefined;
    if (!storage) {
      // HLOS-168-B.1: usar storageFactory() ao invés de OPFSVaultStorage direto.
      // O factory detecta iOS nativo e seleciona CapacitorIOSBackend (Swift bridge),
      // evitando o erro OPFS sandbox deny em iOS 26.6+ (HLOS-168).
      const storeModule = await import("./store");
      storage = options?.backend === "memory"
        ? new storeModule.MemoryVaultStorage()
        : await storageFactoryAsync();
    }

    // HLOS-172 (diagnóstico Bug 2): logar qual backend foi selecionado + path.
    const backendId = getBackendId(storage);
    console.info(
      "[HLOS][vault-adapter] init: backend=%s vaultFile=%s",
      backendId,
      vaultFile,
    );

    const exists = await vaultExists(storage, vaultFile);
    console.info("[HLOS][vault-adapter] init: vaultExists=%s", exists);
    return { exists };
  }

  async create(passphrase: string): Promise<CreateResult> {
    const result = await this.module.Vault.create({
      userId: VAULT_USER_ID,
      passphrase,
      storage: undefined, // usa default OPFS
      vaultData: { createdAt: Date.now(), version: 1 },
    });
    this.currentVault = result.vault;
    return { mnemonic: result.mnemonic };
  }

  async unlock(passphrase: string): Promise<void> {
    // HLOS-168-B.1: storage omitido → Vault.load() usa storageFactory() por default.
    // Em iOS nativo, isso seleciona CapacitorIOSBackend (Swift bridge), bypassando
    // o erro OPFS sandbox deny em iOS 26.6+ (HLOS-168).
    const loaded = await this.module.Vault.load({
      userId: VAULT_USER_ID,
    });
    await loaded.unlockWithPassphrase(passphrase);
    this.currentVault = loaded;
  }

  async unlockWithMnemonic(mnemonic: string): Promise<void> {
    // HLOS-168-B.1: idem unlock() — storage via factory.
    const loaded = await this.module.Vault.load({
      userId: VAULT_USER_ID,
    });
    await loaded.unlockWithMnemonic(mnemonic);
    this.currentVault = loaded;
  }

  lock(): void {
    if (this.currentVault) {
      this.currentVault.lock();
    }
    this.currentVault = null;
  }

  isUnlocked(): boolean {
    return this.currentVault !== null;
  }

  /**
   * HLOS-134 + HLOS-57: extrai master key atual (32 bytes) para biometric setup.
   * AuthState cifra este buffer com chave biométrica (WebAuthn PRF) e DEVE
   * secureWipe() imediatamente após uso.
   */
  getDekBytes(): Uint8Array {
    if (!this.currentVault) {
      throw new Error("vault is locked; cannot getDekBytes");
    }
    // HLOS-57: Vault.getDekHex() retorna string hex; convertemos para bytes.
    const hex = (this.currentVault as unknown as { getDekHex: () => string }).getDekHex();
    if (hex === "anon") {
      // Master key nunca foi definido — vault acabou de unlockar sem DEK?
      throw new Error("vault DEK not available");
    }
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    }
    return bytes;
  }

  /**
   * HLOS-140 + HLOS-57: unlock com master key raw (32 bytes) — fluxo biometric.
   *
   * O `Vault.unlockWithMasterKey()` REAL já existe desde HLOS-51 (decifra o
   * ciphertext diretamente com o DEK; anti-tampering via AES-GCM tag).
   * HLOS-134 deixou aqui um stub quebrado (chamava `unlockWithMnemonic` com
   * mnemonic fictícia — sempre falhava). Este método agora delega ao real.
   *
   * CL-09: erro do vault é vago (anti side-channel) — propagado como vem.
   */
  async unlockWithMasterKey(masterKey: Uint8Array): Promise<void> {
    // HLOS-168-B.1: idem unlock() — storage via factory.
    const loaded = await this.module.Vault.load({
      userId: VAULT_USER_ID,
    });
    await loaded.unlockWithMasterKey(masterKey);
    this.currentVault = loaded;
  }

  readData(): Record<string, unknown> | null {
    if (!this.currentVault) return null;
    try {
      return this.currentVault.readData();
    } catch {
      return null;
    }
  }

  async writeData(data: Record<string, unknown>): Promise<void> {
    if (!this.currentVault) {
      throw new Error("vault is locked; cannot write data");
    }
    await this.currentVault.writeData(data);
  }

  hasOnboardingCompleted(): boolean {
    const data = this.readData();
    return data?.onboardingCompleted === true;
  }

  async markOnboardingCompleted(): Promise<void> {
    if (!this.currentVault) return;
    try {
      const current = this.readData() ?? {};
      await this.writeData({ ...current, onboardingCompleted: true });
    } catch {
      // best-effort: log e segue (constitucional Princípio X)
    }
  }
}

/** User ID constante (single-user, account-less por design). */
const VAULT_USER_ID = "hlos_primary";

/** Verifica se vault existe checando arquivo metadata. */
async function vaultExists(
  storage: import("./store").VaultStorage,
  filePath: string,
): Promise<boolean> {
  const meta = await storage.read(filePath);
  return meta !== null;
}
